import React, { useEffect, useMemo, useCallback } from "react";
import { useState } from "react";
import { classNames } from "../../../../../helpers/classNames";
import { BiFilter } from "react-icons/bi";
import ButtonDropdown from "../../../../ButtonDropdown";
import SmartTableFiltersRow from "./SmartTable___Filters___Row";
import SmartTableFilterGroup from "./SmartTable___FilterGroup";
import { ApiRequest, API_AUTH_TYPES, API_ROUTES } from "../../../../../api";
import { useMutation, useQuery, useQueryClient } from "react-query";

const SmartTableFilters = ({ columns, dbFilters, viewId, tableId, onFiltersChange }) => {
  const usersApi = new ApiRequest(API_ROUTES.USERS, API_AUTH_TYPES.ADVISOR);
  const { data: advisors, isLoading } = useQuery({
    queryFn: usersApi.getFn({ endpoint: 'index_advisors' }),
    queryKey: 'advisors'
  });

  // Convert legacy filter format to new group format
  const convertLegacyFilters = (filters) => {
    // For empty or null filters, return empty array
    if (!filters || (Array.isArray(filters) && filters.length === 0)) {
      return [];
    }

    // If it's already in new group format from DB, return as is
    if (filters.type === 'group') {
      return filters;
    }

    // If it's an array of conditions, return as is (no wrapping!)
    if (Array.isArray(filters)) {
      return filters;
    }

    // Default to empty array
    return [];
  };

  // Convert to save format - much simpler now
  const convertToSaveFormat = (filters) => {
    // If it's a group structure, save as is
    if (filters && filters.type === 'group') {
      return filters;
    }

    // Otherwise, it's an array of conditions, save as is
    return filters || [];
  };

  const [filters, setFilters] = useState(() => convertLegacyFilters(dbFilters));
  const [changesMade, setChangesMade] = useState(false);
  const [open, setOpen] = useState(false);
  const [rootOperator, setRootOperator] = useState('AND'); // Track root level operator

  useEffect(() => {
    // Compare current filter group with database filters
    const currentFilters = filters;
    const dbFilterGroup = convertLegacyFilters(dbFilters);
    setChangesMade(JSON.stringify(currentFilters) !== JSON.stringify(dbFilterGroup));
  }, [filters, dbFilters]);

  useEffect(() => {
    const converted = convertLegacyFilters(dbFilters);
    setFilters(converted);
    // Reset root operator when filters change from DB
    setRootOperator('AND');
  }, [dbFilters]);

  // Reset root operator when filters structure changes
  useEffect(() => {
    if (Array.isArray(filters) && filters.length > 1 && filters.every(c => !c.type || c.type === 'condition')) {
      // Multiple simple conditions at root - keep current operator or default to AND
      if (rootOperator !== 'AND' && rootOperator !== 'OR') {
        setRootOperator('AND');
      }
    } else if (Array.isArray(filters) && filters.length === 1 && filters[0].type === 'group' &&
      filters[0].conditions?.every(c => !c.type || c.type === 'condition')) {
      // Single group with simple conditions - don't change rootOperator as we read from group.operator
    } else {
      // Not multiple simple conditions or single simple group, reset to AND
      setRootOperator('AND');
    }
  }, [filters]);

  const viewsApi = new ApiRequest(
    API_ROUTES.SMART_TABLE_VIEWS,
    API_AUTH_TYPES.ADVISOR
  );

  const handleSaveChanges = async () => {
    try {
      const saveFormat = convertToSaveFormat(filters);
      console.log('Attempting to save filters:', JSON.stringify(saveFormat, null, 2));
      console.log('View ID:', viewId);

      // Save in the appropriate format (array for simple filters, group for complex)
      const response = await viewsApi.patch({
        endpoint: 'update',
        body: {
          column: "filters",
          value: saveFormat,
          id: viewId,
        }
      });

      console.log('Save response:', response);
    } catch (error) {
      console.error("Error saving filters:", error);
      console.error("Error details:", {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
    }
  };

  const queryClient = useQueryClient();
  const { mutate: handleApplyFilterClick, isLoading: isSaving } = useMutation(handleSaveChanges, {
    onSuccess: () => {
      if (onFiltersChange) {
        // Pass the save format to maintain compatibility
        onFiltersChange(convertToSaveFormat(filters));
      }

      // Invalidate the query for the smart table data
      queryClient.invalidateQueries(["smart-table-complete-data", { tableId }]);

      setChangesMade(false);
    },
    onError: (error) => {
      console.error("Error applying filters:", error);
    }
  });

  const countTotalConditions = (filterData) => {
    if (!filterData) return 0;

    // If it's an array of conditions
    if (Array.isArray(filterData)) {
      return filterData.reduce((count, item) => {
        if (item.type === 'group') {
          return count + countTotalConditions(item.conditions || []);
        }
        return count + 1;
      }, 0);
    }

    // If it's a group object
    if (filterData.type === 'group' && filterData.conditions) {
      return countTotalConditions(filterData.conditions);
    }

    return 0;
  };

  const handleAddCondition = () => {
    if (!columns || columns.length === 0) return;
    const firstFilterableColumn = columns.find(col => !["button", "copy_to_clipboard"].includes(col.col_type));
    if (!firstFilterableColumn) return;

    const newCondition = {
      type: 'condition',
      col_id: firstFilterableColumn.col_id,
      operator: "is not empty",
      col_type: firstFilterableColumn.col_type,
      comparison: null,
      options: firstFilterableColumn.options || null,
    };

    setFilters([...filters, newCondition]);
  };

  const handleAddGroup = () => {
    const newGroup = {
      type: 'group',
      operator: 'AND',
      conditions: []
    };

    if (Array.isArray(filters)) {
      setFilters([...filters, newGroup]);
    } else {
      setFilters([newGroup]);
    }
  };

  const handleConditionUpdate = (index, updatedCondition) => {
    if (Array.isArray(filters)) {
      const newFilters = [...filters];
      newFilters[index] = updatedCondition;
      setFilters(newFilters);
    }
  };

  const handleConditionDelete = (index) => {
    if (Array.isArray(filters)) {
      const newFilters = filters.filter((_, i) => i !== index);
      setFilters(newFilters);
    }
  };

  // Check if we have any nested groups or OR logic
  const hasComplexLogic = useMemo(() => {
    if (!filters || !Array.isArray(filters)) return false;
    return filters.some(condition => condition.type === 'group');
  }, [filters]);

  if (!columns) {
    return (
      <button className="rounded text-gray-400 px-2 py-1 flex space-x-1 items-center" disabled>
        <BiFilter className="h-6 w-6" />
        <p className="hidden xl:flex">Filter</p>
      </button>
    );
  }

  const totalConditions = countTotalConditions(filters);

  return (
    <div className="">
      <ButtonDropdown open={open} setOpen={setOpen}>
        <div className="p-3 min-w-[700px] overflow-y-visible">
          <div className="mb-1">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-base font-medium text-gray-900">Filter Configuration</h3>

            </div>
            <p className="text-sm text-gray-500">
              Create filters with AND/OR logic and nested conditional groups
            </p>
          </div>

          {/* Show root level operator selector when there are 2+ simple conditions OR a single root group */}
          {((Array.isArray(filters) && filters.length > 1 &&
            filters.every(c => !c.type || c.type === 'condition')) ||
            (Array.isArray(filters) && filters.length === 1 && filters[0].type === 'group' &&
              filters[0].conditions?.every(c => !c.type || c.type === 'condition'))) && (
              <div className="flex items-center space-x-3 mb-1">
                <select
                  value={Array.isArray(filters) && filters.length === 1 && filters[0].type === 'group'
                    ? filters[0].operator
                    : rootOperator}
                  onChange={(e) => {
                    const newOperator = e.target.value;

                    // Handle single group case
                    if (Array.isArray(filters) && filters.length === 1 && filters[0].type === 'group') {
                      if (newOperator === 'AND') {
                        // Unwrap the group back to simple conditions
                        setFilters(filters[0].conditions);
                        setRootOperator('AND');
                      } else {
                        // Update the group's operator
                        const updatedGroup = { ...filters[0], operator: newOperator };
                        setFilters([updatedGroup]);
                      }
                    } else {
                      // Handle multiple simple conditions case
                      setRootOperator(newOperator);

                      if (newOperator === 'OR') {
                        // Wrap conditions in a group when OR is selected
                        const wrappedGroup = {
                          type: 'group',
                          operator: 'OR',
                          conditions: filters
                        };
                        setFilters([wrappedGroup]);
                      }
                      // If AND is selected, keep as simple array (current behavior)
                    }
                  }}
                  className="text-sm py-1  bg-transparent border-none focus:ring-0 focus:ring-offset-0"
                >
                  <option value="AND">ALL</option>
                  <option value="OR">ANY</option>
                </select>
                <span className="text-sm -ml-1 text-gray-600">of the following conditions must be met:</span>
              </div>

            )}



          <div className="space-y-1">
            {Array.isArray(filters) && filters.map((condition, index) => (
              <div key={`${condition.col_id || 'group'}-${index}`}>
                {(!condition.type || condition.type === 'condition') ? (
                  <SmartTableFiltersRow
                    filters={{
                      array: filters,
                      update: (idx, newCondition) => handleConditionUpdate(idx, newCondition),
                      remove: (idx) => handleConditionDelete(idx)
                    }}
                    advisors={advisors}
                    columns={columns}
                    filter={condition}
                    thisFilterIndex={index}
                    isInGroup={false}
                    groupOperator="AND"
                  />
                ) : (
                  <SmartTableFilterGroup
                    filterGroup={condition}
                    onUpdate={(updatedGroup) => handleConditionUpdate(index, updatedGroup)}
                    onDelete={() => handleConditionDelete(index)}
                    columns={columns}
                    advisors={advisors}
                    level={0}  // Start at level 0 for nested groups since root is transparent
                    showDelete={true}
                  />
                )}
              </div>
            ))}
          </div>

          {(!filters || (Array.isArray(filters) && filters.length === 0)) && (
            <div className="text-center py-8 text-gray-500">
              <p className="text-lg mb-2">No filters applied</p>
              <p className="text-sm">Click + Add Condition to start filtering your data</p>
            </div>
          )}

          <div className="flex justify-between items-center mt-4 pt-3 border-t border-gray-200">
            <div className="flex items-center space-x-2">
              <button
                onClick={handleAddCondition}
                className="text-xs font-medium text-gray-700 hover:text-gray-900"
                disabled={isSaving}
              >
                + Add Condition
              </button>

              <button
                onClick={handleAddGroup}
                className="text-xs font-medium text-gray-700 hover:text-gray-900"
                disabled={isSaving}
                title="Add a new empty conditional group at the current level"
              >
                + Add Conditional Group
              </button>


            </div>

            <div className="flex items-center space-x-2">
              <button
                onClick={() => {
                  setFilters([]);
                }}
                className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                disabled={isSaving}
              >
                Clear All
              </button>

              <button
                onClick={handleApplyFilterClick}
                disabled={isSaving || !changesMade}
                className={classNames(
                  'inline-flex items-center px-4 py-2 text-sm font-medium border rounded-md transition-colors duration-150',
                  isSaving ? 'bg-gray-200 text-gray-500 cursor-not-allowed border-gray-200' :
                    changesMade ? 'bg-teal-600 hover:bg-teal-700 text-white border-teal-600 animate-pulse' : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border-gray-300'
                )}
              >
                {isSaving ? 'Saving...' : (changesMade ? 'Apply Filters' : 'Applied')}
              </button>
            </div>
          </div>
        </div>
      </ButtonDropdown>

      <button
        onClick={() => setOpen(true)}
        className={classNames(
          "rounded hover:ring-2 hover:ring-gray-300 text-gray-600 px-2 py-1 flex space-x-1 items-center",
          (totalConditions > 0) && "bg-teal-100",
          changesMade && "ring-2 ring-teal-400 ring-offset-1"
        )}
      >
        <BiFilter className="h-6 w-6" />
        <p className="hidden xl:flex">
          {totalConditions === 0
            ? "Filter"
            : `${totalConditions} filter${totalConditions > 1 ? "s" : ""}`}
          {changesMade && !isSaving && ' *'}
        </p>
      </button>
    </div>
  );
};

export default SmartTableFilters;
